import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useApp } from '../components/AppProvider';
import { OAuthCallbackLayout } from '../components/oauth/OAuthCallbackLayout';
import { getStoredPkceVerifier } from '../lib/microsoft/microsoftScopes';

export function MicrosoftAuthCallback() {
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useApp();

  // Debug del componente
  console.log('MicrosoftAuthCallback component rendered', {
    location: window.location.href,
    params: location.search,
    hasCode: location.search.includes('code=')
  });

  // Utilizziamo una ref per tracciare se l'operazione è già stata eseguita
  const authProcessedRef = React.useRef(false);

  // Attiva il logging di debug
  const debugLogEnabled = true;

  useEffect(() => {
    // Previeni esecuzioni multiple dello stesso effetto
    if (authProcessedRef.current) {
      return;
    }

    async function handleAuthCallback() {
      try {
        // Marca l'operazione come già eseguita
        authProcessedRef.current = true;

        // Parse URL params
        const params = new URLSearchParams(location.search);
        const code = params.get('code');
        const errorParam = params.get('error');
        const errorDescription = params.get('error_description');
        const state = params.get('state');

        if (errorParam) {
          const errorMsg = errorDescription
            ? `${errorParam}: ${errorDescription}`
            : errorParam;
          setError(`Errore di autenticazione Microsoft: ${errorMsg}`);
          setProcessing(false);
          return;
        }

        if (!code) {
          setError('Nessun codice di autorizzazione ricevuto da Microsoft');
          setProcessing(false);
          return;
        }

        // Exchange the code for tokens using a Supabase function
        console.log('Exchanging code for tokens, code length:', code?.length, 'code preview:', code?.substring(0, 10) + '...');

        // Verifica che la sessione utente sia valida prima di continuare
        const { data: { session } } = await supabase.auth.getSession();
        console.log('User session exists:', !!session, 'User logged in:', !!session?.user);

        if (!session || !session.user) {
          console.error('No active session found, cannot connect Microsoft account');
          setError('Impossibile connettere l\'account Microsoft: sessione non attiva. Effettua nuovamente il login.');
          setProcessing(false);
          return;
        }

        try {
          // Recupera il token di accesso per la chiamata alla funzione Supabase
          const accessToken = session.access_token;
          console.log('Access token available:', !!accessToken);

          // Redirect URI deve essere lo stesso usato nella richiesta di autorizzazione
          const redirectUri = import.meta.env.VITE_MICROSOFT_REDIRECT_URI || `${window.location.origin}/digital-microsoft-connect`;

          // PKCE: recupera (e rimuove) il verifier salvato prima del redirect.
          // Se assente (redirect avviato da versione vecchia della pagina in cache),
          // si procede senza code_verifier per retrocompatibilità.
          const codeVerifier = getStoredPkceVerifier();

          console.log('Sending request to microsoft-auth-token function with these parameters:',
            {
              code_length: code?.length,
              redirect_uri: redirectUri,
              has_code_verifier: !!codeVerifier
            }
          );

          // Dobbiamo usare l'edge function per ottenere il refresh token e i dati utente di Microsoft
          // Non possiamo farlo direttamente dal browser per motivi di sicurezza
          console.log('Iniziando lo scambio del codice tramite la funzione edge microsoft-auth-token');

          // Ottieni i token da Microsoft tramite la nostra funzione Edge
          const { data, error: tokenError } = await supabase.functions.invoke('microsoft-auth-token', {
            body: {
              code,
              // Deve essere esattamente lo stesso URL usato nella chiamata di autorizzazione
              redirectUri,
              // PKCE: incluso solo se presente (retrocompatibilità con redirect legacy senza challenge)
              ...(codeVerifier ? { code_verifier: codeVerifier } : {})
            }
          });

          if (tokenError || !data) {
            console.error('Errore durante lo scambio del codice:', tokenError || 'Nessun dato ricevuto');
            throw new Error('Non è stato possibile ottenere i token da Microsoft. ' +
              (tokenError ? tokenError.message : 'Nessun dato ricevuto'));
          }

          // Log molto dettagliato per debug
          if (debugLogEnabled) {
            console.log('===== DEBUG TOKEN RESPONSE =====');
            console.log('Token ricevuti con successo:', {
              hasRefreshToken: !!data.refresh_token,
              refreshTokenLength: data.refresh_token?.length,
              hasAccessToken: !!data.access_token,
              accessTokenLength: data.access_token?.length,
              hasUserInfo: !!data.user_info,
              userInfoEmail: data.user_info?.mail || data.user_info?.userPrincipalName,
              userInfoId: data.user_info?.id,
              userId: data.user_id,
              scopes: data.scopes,
              serverTime: data.server_time
            });

            // Log completo dell'oggetto (attenzione a non loggare in produzione)
            console.log('Token data completo (SOLO PER DEBUG):', JSON.stringify(data, null, 2));
          }

          // Verifica che abbiamo ricevuto i dati necessari
          if (!data.user_info) {
            throw new Error('Dati incompleti ricevuti da Microsoft. ' +
              'Mancano le informazioni utente.');
          }

          // È possibile che Microsoft non fornisca un nuovo refresh token se l'utente ha già autorizzato l'app
          // In questo caso, dobbiamo gestire correttamente la situazione
          if (!data.refresh_token) {
            console.warn('Nessun refresh token ricevuto da Microsoft. Questo può succedere se l\'utente ha già autorizzato l\'app.');

            // Verifichiamo se l'utente ha già un refresh token salvato
            const { data: existingProfile, error: profileError } = await supabase
              .from('user_profiles')
              .select('microsoft_refresh_token, microsoft_account_connected')
              .eq('id', session.user.id)
              .single();

            if (profileError) {
              console.error('Errore nel verificare il profilo esistente:', profileError);
              throw new Error('Impossibile verificare se esiste già un refresh token salvato.');
            }

            if (!existingProfile.microsoft_refresh_token) {
              console.error('L\'utente non ha un refresh token salvato e Microsoft non ne ha fornito uno nuovo.');
              throw new Error('Microsoft non ha fornito un refresh token e non ne è stato trovato uno esistente. ' +
                'Prova a disconnettere e riconnettere l\'account Microsoft, oppure usa un altro account Microsoft.');
            }

            console.log('Utilizzo del refresh token esistente poiché Microsoft non ne ha fornito uno nuovo.');
            // Assegniamo il token salvato ai dati per l'aggiornamento
            data.refresh_token = existingProfile.microsoft_refresh_token;
          }

          // Microsoft Graph API restituisce 'mail' per account work/school e 'userPrincipalName' per account personali
          const microsoftEmail = data.user_info.mail || data.user_info.userPrincipalName;

          // Prepara i dati per l'aggiornamento del profilo utente
          const updateData = {
            microsoft_account_connected: true,
            microsoft_refresh_token: data.refresh_token,
            microsoft_account_email: microsoftEmail,
            microsoft_user_id: data.user_info.id,
            microsoft_scopes: data.scopes || [],
            updated_at: new Date().toISOString()
          };

          if (debugLogEnabled) {
            console.log('===== DEBUG UPDATE PROFILE =====');
            console.log('Dati preparati per aggiornamento profilo:', {
              userId: session.user.id,
              connected: updateData.microsoft_account_connected,
              email: updateData.microsoft_account_email,
              microsoftUserId: updateData.microsoft_user_id,
              hasRefreshToken: !!updateData.microsoft_refresh_token,
              refreshTokenLength: updateData.microsoft_refresh_token?.length,
              hasScopes: !!updateData.microsoft_scopes && updateData.microsoft_scopes.length > 0
            });
          }

          // Verifica che l'utente esista prima dell'aggiornamento
          const { data: existingUser, error: getUserError } = await supabase
            .from('user_profiles')
            .select('id, email')
            .eq('id', session.user.id)
            .single();

          if (getUserError) {
            console.error('Errore nel verificare l\'esistenza del profilo utente:', getUserError);
            throw new Error(`Impossibile verificare il profilo utente: ${getUserError.message}`);
          }

          console.log('Profilo utente esistente:', existingUser);

          // Salva i dati dell'account Microsoft nel profilo utente
          const { data: updatedProfile, error: updateError } = await supabase
            .from('user_profiles')
            .update(updateData)
            .eq('id', session.user.id)
            .select();

          if (updateError) {
            console.error('Errore durante l\'aggiornamento del profilo:', updateError);
            throw new Error(`Errore nell'aggiornamento del profilo: ${updateError.message}`);
          }

          console.log('Profilo aggiornato con successo:', {
            success: !!updatedProfile,
            rowsUpdated: updatedProfile?.length
          });

          console.log('Account Microsoft collegato con successo al profilo:', {
            userId: session.user.id,
            microsoftEmail: microsoftEmail,
            microsoftId: data.user_info.id
          });

          // Notifica all'utente
          toast.success('Account Microsoft collegato con successo!');

          // Parse the state to determine where to navigate
          let redirectTo = '/settings?tab=microsoft-workspace';

          try {
            console.log('Trying to parse state:', state);
            if (state) {
              const decodedState = decodeURIComponent(state);
              const stateObj = JSON.parse(decodedState);
              console.log('Parsed state object:', stateObj);

              if (stateObj.redirectTo) {
                redirectTo = stateObj.redirectTo;
                console.log('Setting redirect to:', redirectTo);
              }
            }
          } catch (e) {
            console.error('Error parsing state:', e);
          }

          // Utilizziamo la history API per sostituire l'URL corrente
          // Questo rimuove i parametri di query di Microsoft dall'URL
          window.history.replaceState({}, '', redirectTo);

          // Aggiorna lo stato dell'URL e naviga alla destinazione finale
          setTimeout(() => {
            navigate(redirectTo, { replace: true });
          }, 1000);

          return;
        } catch (err: unknown) {
          const errorMessage = err instanceof Error ? err.message : 'Errore sconosciuto';
          console.error('Exception during token exchange:', err);
          setError(`Errore durante lo scambio del token: ${errorMessage}`);
          setProcessing(false);
          return;
        }
      } catch (err) {
        console.error('Error in Microsoft authentication callback:', err);
        setError('Si è verificato un errore imprevisto. Riprova.');
        setProcessing(false);
        // Prevenire navigazione automatica in caso di errore
        return;
      }
    }

    handleAuthCallback();
  }, [location, navigate, toast]);

  const microsoftLogo = (
    <svg className="h-10 w-10" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );

  if (processing && !error) {
    return (
      <OAuthCallbackLayout
        status="processing"
        title="Connessione a Microsoft in corso"
        description="Stiamo completando il collegamento del tuo account Microsoft."
        providerLogo={microsoftLogo}
      />
    );
  }

  if (error) {
    return (
      <OAuthCallbackLayout
        status="error"
        title="Connessione non riuscita"
        description={error}
        providerLogo={microsoftLogo}
        primaryAction={{
          label: 'Torna alla dashboard',
          onClick: () => navigate('/'),
        }}
        secondaryAction={{
          label: 'Riprova',
          onClick: () => navigate('/settings?tab=microsoft-workspace'),
        }}
      />
    );
  }

  return (
    <OAuthCallbackLayout
      status="success"
      title="Account Microsoft collegato"
      description="Il tuo account Microsoft è stato collegato correttamente. Ti stiamo reindirizzando..."
      providerLogo={microsoftLogo}
    />
  );
}
